import { TabsContent } from "@/Components/ui/tabs"
import PaginatedFirearmGrid from "@/Components/shared/PaginatedFirearmGrid"

/**
 * Shared bodies for the All / Active / Sold listing tabs used by the auction,
 * brand, brand-facet and category pages. Pages keep their own page chrome
 * (`Tabs`, `TabsList`, sidebar, carousels) and drop this in where the three
 * `TabsContent` blocks belong.
 *
 * The `emptyState` node renders in every tab whose content is empty, so a
 * filtered-to-nothing listing surfaces the same notice/clear-filters affordance
 * no matter which tab is open.
 *
 * Behaviour:
 * - All:    active auctions infinite-scroll first; once every active page has
 *           loaded, the recently-sold section always follows beneath them. With
 *           no active auctions the empty state shows in their place, with
 *           recently-sold beneath it.
 * - Active: when there are no active auctions, the empty state renders with the
 *           recently-sold section beneath it; otherwise active auctions
 *           infinite-scroll.
 * - Sold:   the recently-sold section renders; when it is empty the active
 *           auctions infinite-scroll beneath a "no sold" notice, and when both
 *           are empty the empty state shows instead.
 */
export default function ListingTabBodies({
    liveAuctions = [],
    liveMeta,
    recentlySold = [],
    soldMeta,
    liveTitle,
    liveSubtitle,
    soldTitle = "Recently Sold",
    liveEmptyMessage,
    soldEmptyMessage,
    emptyState = null,
    contentClassName = "w-full",
}) {
    const liveTotal = liveMeta?.total ?? 0
    const soldTotal = soldMeta?.total ?? 0
    const liveCurrentPage = liveMeta?.current_page ?? 1
    const liveLastPage = liveMeta?.last_page ?? 1

    // In the "all" tab the recently-sold section is revealed only once every
    // active page has been loaded (or there are no active auctions at all).
    const liveExhausted = liveTotal === 0 || liveCurrentPage >= liveLastPage

    const liveGrid = () => (
        <PaginatedFirearmGrid
            title={liveTitle}
            subtitle={liveSubtitle}
            items={liveAuctions}
            meta={liveMeta}
            pageName="live_page"
            mode="infinite"
            emptyMessage={liveEmptyMessage}
        />
    )

    const soldGrid = () => (
        <PaginatedFirearmGrid
            title={soldTitle}
            items={recentlySold}
            meta={soldMeta}
            pageName="sold_page"
            mode="infinite"
            emptyMessage={soldEmptyMessage}
        />
    )

    return (
        <>
            <TabsContent value="all" className={contentClassName}>
                <div className="flex flex-col w-full gap-12">
                    {liveTotal > 0 ? liveGrid() : emptyState}
                    {liveExhausted && soldTotal > 0 && soldGrid()}
                </div>
            </TabsContent>

            <TabsContent value="active" className={contentClassName}>
                <div className="flex flex-col w-full gap-12">
                    {liveTotal === 0 ? (
                        <>
                            {emptyState}
                            {soldTotal > 0 && soldGrid()}
                        </>
                    ) : (
                        liveGrid()
                    )}
                </div>
            </TabsContent>

            <TabsContent value="sold" className={contentClassName}>
                <div className="flex flex-col w-full gap-12">
                    {soldTotal === 0 && liveTotal === 0 ? (
                        emptyState
                    ) : (
                        <>
                            {soldGrid()}
                            {soldTotal === 0 && liveTotal > 0 && liveGrid()}
                        </>
                    )}
                </div>
            </TabsContent>
        </>
    )
}
